import { useNavigate } from 'react-router-dom'
import { MENU_NAMES } from 'texts/menuNames'
import { Typography } from 'ui/atoms/Typography'
import { Button } from 'ui/atoms/Button'
import { DELETE_LOCATION_MODAL, LIST_OF_LOCATIONS } from 'texts/locationList'
import { useEffect, useMemo, useState } from 'react'
import { locationsApi, LocationsApiTypes } from 'api'
import { Checkbox, LazyImage } from '@frontend/design_system'
import { COUNTRY_INDEX, headItems, LOCATION_INDEX } from './constants'
import warningIcon from 'assets/icons/yellow700/yellow700_warning_20x20.webp'
import { Table } from 'ui/molecules/Table'
import { Toggle } from 'ui/atoms/Toggle'
import { breadcrumbs } from 'constants/breadcrumbs'
import { ModalWrapper } from './components/ModalWrapper'
import { useAppSelector } from 'redux/hooks'
import {
  isMigratedUserSelector,
  isOperatorSelector,
  userDataSelector,
} from 'redux/login/selectors'
import { VehicleListModal } from './components/VehicleListModal'
import { EquipmentListModal } from './components/EquipmentListModal'
import { Container } from 'ui/molecules/Container'
import { useDispatch, useSelector } from 'react-redux'
import { setNotificationMessage } from 'redux/notifications/slice'
import { PAGINATION_DROPDOWN_LABEL, TOTAL } from 'texts/uiTexts'
import { URLS } from 'constants/urls'
import { useApiRequest } from 'hooks/useApiRequest'
import styles from './styles.module.scss'
import {
  LOCATION_EDIT_CONFIRMATION,
  SUCCESS_ACTIVATE,
  SUCCESS_INACTIVATE,
} from 'texts/locationEdit'
import { Confirmation } from 'ui/molecules/Confirmation'
import { HirePoint } from 'api/locations/types'
import { GTM } from 'utils/gtm'
import { DATA_LAYER_EVENT } from 'constants/dataLayerEvents'
import { OperatorActionButtons } from 'ui/components/OperatorActionButtons'
import { TableHeadProps } from 'ui/molecules/Table/TableHead/types'
import { sortingSelector } from 'redux/sorting/selectors'
import {
  filterItemsSelector,
  filtersCompanyIdSelector,
  selectedFiltersCountSelector,
  selectedFiltersSelector,
} from 'redux/filters/selectors'
import { useFilterSettings } from 'hooks/useFilterSettings'
import { LOCATIONS_FILTER_KEY } from 'constants/filters'
import { resetFiltersByKey, setFilterItems } from 'redux/filters/slice'
import { prepareFilterItems } from 'utils/filters'
import { prepareFiltersForReq, prepareSortingForReq } from 'utils/table'
import { ResetFiltersButton } from 'ui/components/ResetFiltersButton'
import { useCompanyId } from 'hooks/useCompanyId'
import { ReviewBlock } from 'ui/components/ReviewBlock/ReviewBlock'
import { ACCOUNT_STATUSES, REGISTRATION_STATUSES } from 'api/auth/constants'
import { Pagination } from 'ui/molecules/Pagination'
import { usePagination } from 'hooks/usePagination'
import { PlusIcon } from 'ui/icons'
import { MissingFieldsBadge } from 'ui/components/MissingFieldsBadge'
import { useCompanyInfo } from 'ui/hooks/useCompanyInfo'

export const LocationsList = () => {
  const dispatch = useDispatch()
  const [locationsList, setLocationsList] =
    useState<LocationsApiTypes.GetHirePointsResponse | null>(null)
  const { accountStatus, sectionsStatuses } = useAppSelector(userDataSelector)
  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false)
  const [selectedLocation, setSelectedLocation] = useState<HirePoint>()
  const [showLocationsRequiredToFillIn, setShowLocationsRequiredToFillIn] =
    useState(false)
  const isMigratedUser = useSelector(isMigratedUserSelector)
  const isOperator = useAppSelector(isOperatorSelector)
  const sortingData = useSelector(sortingSelector)
  const selectedFiltersCount = useSelector(selectedFiltersCountSelector)
  const selectedFilters = useSelector(selectedFiltersSelector)
  const filterItems = useSelector(filterItemsSelector)
  const filtersCompanyId = useSelector(filtersCompanyIdSelector)

  const navigate = useNavigate()

  const { page, pageSize, handleResetPagination } = usePagination()

  const getFilterSettings = useFilterSettings(
    LOCATIONS_FILTER_KEY,
    handleResetPagination
  )

  const breadcrumbsList = [breadcrumbs.profileList]

  const updateCompanyStatusRequest = useApiRequest(
    (id, isActive) => locationsApi.changeHirePointState(id, isActive),
    undefined,
    true
  )

  const { getCompanyInfo } = useCompanyInfo(false)

  const isLocationsReady = [
    REGISTRATION_STATUSES.APPROVED,
    REGISTRATION_STATUSES.REVIEW,
  ].includes(sectionsStatuses?.locationsStatus || '')

  const isLocationsInReview =
    isLocationsReady &&
    accountStatus !== ACCOUNT_STATUSES.APPROVED &&
    !isOperator

  const getHirePointListRequest = useApiRequest((companyId) =>
    locationsApi.getHirePointsList(
      page - 1,
      Number(pageSize),
      Number(companyId),
      showLocationsRequiredToFillIn,
      prepareFiltersForReq(
        selectedFilters.locations,
        selectedFiltersCount.locations,
        LOCATIONS_FILTER_KEY
      ),
      prepareSortingForReq(sortingData.locations, LOCATIONS_FILTER_KEY)
    )
  )

  const getLocationsFiltersRequest = useApiRequest((companyId) =>
    locationsApi.getLocationsFilters(companyId)
  )

  const companyId = useCompanyId()

  const isFiltersFromOtherCompany =
    !!selectedFiltersCount.locations &&
    filtersCompanyId.locations !== Number(companyId)

  const handleAddLocation = () => {
    navigate('add')
  }

  const fetchHirePoints = async () => {
    const hirePointListResponse = await getHirePointListRequest.apiRequest(
      Number(companyId),
      showLocationsRequiredToFillIn
    )
    if (hirePointListResponse) setLocationsList(hirePointListResponse.data)
  }

  useEffect(() => {
    if (
      !isLocationsReady &&
      accountStatus !== ACCOUNT_STATUSES.APPROVED &&
      !isOperator
    ) {
      handleAddLocation()
    }
  }, [sectionsStatuses])

  const fetchFilters = async () => {
    const locationsFilters = await getLocationsFiltersRequest.apiRequest(
      companyId
    )

    if (locationsFilters) {
      dispatch(
        setFilterItems({
          items: prepareFilterItems(locationsFilters.data),
          key: LOCATIONS_FILTER_KEY,
          companyId: Number(companyId),
        })
      )
    }
  }

  useEffect(() => {
    if (isFiltersFromOtherCompany) {
      dispatch(resetFiltersByKey(LOCATIONS_FILTER_KEY))
    }
  }, [companyId])

  useEffect(() => {
    if (companyId && !isFiltersFromOtherCompany) {
      fetchHirePoints()
    }
  }, [
    page,
    pageSize,
    companyId,
    sortingData,
    selectedFiltersCount.locations,
    showLocationsRequiredToFillIn,
  ])

  useEffect(() => {
    !selectedFiltersCount.locations && fetchFilters()
  }, [selectedFiltersCount.locations])

  const handleChangeHirePointStatus = async () => {
    if (selectedLocation) {
      const response = await updateCompanyStatusRequest.apiRequest(
        selectedLocation.id,
        selectedLocation.isClosed
      )
      if (response) {
        dispatch(
          setNotificationMessage({
            notificationMessage: selectedLocation.isClosed
              ? SUCCESS_ACTIVATE
              : SUCCESS_INACTIVATE,
          })
        )
        if (isMigratedUser) {
          getCompanyInfo(Number(companyId))
        }
        if (!selectedLocation.isClosed) {
          GTM.dataLayer({
            event: DATA_LAYER_EVENT.LOCATION_REMOVED,
            location_name: selectedLocation.locationName,
            location_country: selectedLocation.country,
            location_city: selectedLocation.city,
          })
        }
        fetchHirePoints()
        setConfirmationModalOpen(false)
      }
    }
  }

  const handleRowClick = (id: number) => {
    navigate(
      `${URLS.LOCATIONS}${URLS.LIST_OF_LOCATIONS}/${companyId}/${id}/edit`
    )
  }

  const handleDeleteLocation = (location: HirePoint) => {
    setConfirmationModalOpen(true)
    setSelectedLocation(location)
  }

  const formattedBodyItems = locationsList?.hirePoints.pageItems?.map(
    (hirePoint, i) => {
      const isCompanyActive = !hirePoint.isClosed
      return {
        items: [
          <Typography className={styles.country} name="Subtitle7" Tag="div">
            {hirePoint.hasEmptyFields && (
              <LazyImage src={warningIcon} width={20} height={20} />
            )}
            {hirePoint.country}
          </Typography>,
          hirePoint.locationName,
          <ModalWrapper
            label={hirePoint.availableCarsCount}
            isEmptyModal={!hirePoint.availableCarsCount}
            companyId={Number(companyId)}
            hirePointId={hirePoint.id}
            Modal={VehicleListModal}
          />,
          <ModalWrapper
            label={hirePoint.availableEquipmentsCount}
            isEmptyModal={!hirePoint.availableEquipmentsCount}
            companyId={Number(companyId)}
            hirePointId={hirePoint.id}
            Modal={EquipmentListModal}
          />,
          <div className={styles.toggle}>
            <Toggle
              key={i}
              value={isCompanyActive}
              onChange={() => handleDeleteLocation(hirePoint)}
            />
          </div>,
        ],
        onClick: () => handleRowClick(hirePoint.id),
      }
    }
  )

  const isLocationsWithEmptyFieldsExist = useMemo(
    () =>
      locationsList?.hirePoints.pageItems?.some(
        (hirePoint) => hirePoint.hasEmptyFields
      ),
    [locationsList]
  )

  const formattedHeadItems = useMemo(() => {
    const newHeadItems: TableHeadProps['item'][] = headItems.map((header) => ({
      value: header,
    }))
    newHeadItems[COUNTRY_INDEX].filterSettings = getFilterSettings('countries')
    newHeadItems[LOCATION_INDEX].filterSettings = getFilterSettings('locations')

    return newHeadItems
  }, [filterItems.profileList, getFilterSettings])

  const header = (
    <>
      {isConfirmationModalOpen && (
        <Confirmation
          confirmModalProps={{
            question: `${
              LOCATION_EDIT_CONFIRMATION.CONFIRMATION_MODAL.QUESTION
            } ${selectedLocation?.locationName}${
              !selectedLocation?.isClosed
                ? LOCATION_EDIT_CONFIRMATION.CONFIRMATION_MODAL.INACTIVE
                : LOCATION_EDIT_CONFIRMATION.CONFIRMATION_MODAL.ACTIVATE
            }`,
            onConfirm: handleChangeHirePointStatus,
            onClose: () => setConfirmationModalOpen(false),
            title: !selectedLocation?.isClosed
              ? `${DELETE_LOCATION_MODAL.DEACTIVATE} ${selectedLocation?.locationName}`
              : `${DELETE_LOCATION_MODAL.ACTIVATE} ${selectedLocation?.locationName}`,
          }}
        />
      )}
      <div className={styles.title}>
        <Typography Tag="h5" name="H5">
          {MENU_NAMES.LOCATIONS}
        </Typography>
      </div>
    </>
  )

  return (
    <>
      <Container
        breadcrumbList={isOperator ? breadcrumbsList : []}
        currentPageLabel={MENU_NAMES.LOCATIONS}
        title={header}
        tooltip={LIST_OF_LOCATIONS.TOOLTIP_CONTENT}
        actionButtons={(id: string) => (
          <div className={styles['right-side']}>
            <OperatorActionButtons companyId={id} section="Locations" />
          </div>
        )}
        withNavigation={isOperator}
        loading={
          getHirePointListRequest.loading && !getHirePointListRequest.isLoaded
        }
        rightBlock={
          isMigratedUser &&
          isLocationsWithEmptyFieldsExist && (
            <Checkbox
              checked={showLocationsRequiredToFillIn}
              onChange={setShowLocationsRequiredToFillIn}
              variant="primary"
              borderVariant="grey"
              label={LIST_OF_LOCATIONS.CHECKBOX_LABEL}
              size="secondary"
              typographyName="body2WMedium"
            />
          )
        }
      >
        {isLocationsInReview ? (
          <ReviewBlock
            isApproved={
              sectionsStatuses?.locationsStatus ===
              REGISTRATION_STATUSES.APPROVED
            }
          />
        ) : (
          <>
            {isLocationsWithEmptyFieldsExist && !isOperator && (
              <MissingFieldsBadge
                message={LIST_OF_LOCATIONS.MISSING_FIELDS_MESSAGE}
              />
            )}
            <div className={styles.list}>
              <div className={styles.header}>
                <Typography name="Subtitle2">{`${TOTAL}: ${locationsList?.hirePoints.totalItems}`}</Typography>
                <div className={styles['action-buttons']}>
                  <ResetFiltersButton
                    selectedFiltersCount={selectedFiltersCount.locations}
                  />
                  <div className={styles.button}>
                    <Button
                      size="small"
                      typographyName="Button2"
                      variant="outline"
                      onClick={handleAddLocation}
                      label={LIST_OF_LOCATIONS.BUTTON_LABEL}
                      iconPosition="right"
                      icon={<PlusIcon color="blue700" />}
                    />
                  </div>
                </div>
              </div>
              {formattedBodyItems && (
                <div className={styles.table}>
                  <Table
                    headItems={formattedHeadItems}
                    bodyItems={formattedBodyItems}
                    initItemsWidth={['276', '276', '110', '110', '81']}
                  />
                </div>
              )}
            </div>
            <footer className={styles.pagination}>
              <Pagination
                defaultPage={page}
                defaultPageSize={Number(pageSize)}
                pageItemCount={locationsList?.hirePoints.pageItems.length}
                total={locationsList?.hirePoints?.totalItems || 0}
                pageSizeLabel={PAGINATION_DROPDOWN_LABEL}
              />
            </footer>
          </>
        )}
      </Container>
    </>
  )
}
